/**
 * Typed fetch client for the Cloudflare Workers API.
 *
 * - Base URL comes from import.meta.env.VITE_API_URL (see .env.example).
 * - Bearer token is injected from a pluggable token provider (wired to the auth
 *   store in src/auth/store.tsx) so this module stays free of React imports.
 * - All responses are parsed as the shared DTO types; non-2xx responses are
 *   thrown as ApiClientError carrying the parsed `ApiError` envelope.
 *
 * The endpoint helpers mirror §3 of APP_BUILDER_BACKEND_DESIGN.md. Core paths
 * (auth, apps/config, assets, builds, billing, entitlement) are implemented;
 * secondary admin/recovery paths are stubbed with TODOs.
 */
import type {
  AppConfig,
  AppSummary,
  AppVersionDto,
  AuthTokens,
  BuildDto,
  BuildRequest,
  CreateAppRequest,
  EntitlementDto,
  SigningMode,
  SigningStatusDto,
  ApiError,
  ErrorCode,
} from '@appbuilder/shared';

const API_URL: string = import.meta.env.VITE_API_URL ?? '/v1';
const DEVICE_KEY = 'appbuilder.deviceId'; // set by the mobile web-handoff (per-device build quota)

/** Error thrown for any non-2xx API response. Carries the structured envelope. */
export class ApiClientError extends Error {
  readonly status: number;
  readonly code: ErrorCode | string;
  readonly details?: unknown;

  constructor(status: number, body: ApiError | undefined, fallbackMessage: string) {
    const message = body?.error?.message ?? fallbackMessage;
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = body?.error?.code ?? 'unknown';
    this.details = body?.error?.details;
  }

  /** True when the failure is an expired/invalid session (drives logout). */
  get isUnauthenticated(): boolean {
    return this.status === 401 || this.code === 'unauthenticated';
  }

  /** True when the account lacks the entitlement to build (HTTP 402). */
  get isNoEntitlement(): boolean {
    return this.status === 402 || this.code === 'no_entitlement';
  }

  /** True when the device's free daily build quota is spent (HTTP 429) — drives the upgrade CTA. */
  get isQuotaExceeded(): boolean {
    return this.status === 429 || this.code === 'build_quota_exceeded';
  }
}

// ─── Token wiring ────────────────────────────────────────────────────────────

type TokenProvider = () => string | null;
type UnauthorizedHandler = () => void;

let getToken: TokenProvider = () => null;
let onUnauthorized: UnauthorizedHandler = () => {};

/** Called once at app start by the auth store to wire token + 401 handling. */
export function configureClient(provider: TokenProvider, unauthorized: UnauthorizedHandler): void {
  getToken = provider;
  onUnauthorized = unauthorized;
}

// ─── Core request helper ─────────────────────────────────────────────────────

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Extra headers (e.g. Idempotency-Key for build trigger). */
  headers?: Record<string, string>;
  /** Skip the Authorization header (login/signup). */
  anonymous?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...opts.headers,
  };

  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (!opts.anonymous) {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    // Attribute builds to the phone that handed off (falls back to per-account server-side).
    if (!headers['X-Device-Id']) {
      const deviceId = typeof localStorage !== 'undefined' ? localStorage.getItem(DEVICE_KEY) : null;
      if (deviceId) headers['X-Device-Id'] = deviceId;
    }
  }

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (cause) {
    // Network-level failure (offline, DNS, CORS preflight, abort).
    throw new ApiClientError(0, undefined, `Network error: ${(cause as Error).message}`);
  }

  if (res.status === 401) {
    onUnauthorized();
  }

  // 204 No Content / empty body.
  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  const parsed = text ? safeJson(text) : undefined;

  if (!res.ok) {
    throw new ApiClientError(res.status, parsed as ApiError | undefined, res.statusText || 'Request failed');
  }

  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

// ─── Auth (§3.1) ─────────────────────────────────────────────────────────────

export interface LoginRequest {
  email: string;
  password: string;
}

export interface SignupRequest {
  email: string;
  password: string;
  displayName?: string;
}

/** Shape returned by GET /v1/auth/me (user + entitlement summary). */
export interface MeResponse {
  user: { id: string; email: string; role: string; displayName?: string };
  entitlement: EntitlementDto;
}

/** POST /v1/auth/{login,signup} return the user plus a nested tokens object. */
export interface AuthResult {
  user: { id: string; email: string; accountId: string; role: string };
  tokens: AuthTokens & { refreshToken: string };
}

export const authApi = {
  login: (req: LoginRequest) =>
    request<AuthResult>('/auth/login', { method: 'POST', body: req, anonymous: true }),

  signup: (req: SignupRequest) =>
    request<AuthResult>('/auth/signup', { method: 'POST', body: req, anonymous: true }),

  me: () => request<MeResponse>('/auth/me'),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  /**
   * Request a password-reset email. Anonymous and enumeration-safe: the API
   * always returns {ok:true} regardless of whether the email exists.
   */
  requestPasswordReset: (email: string) =>
    request<{ ok: boolean }>('/auth/password/reset-request', {
      method: 'POST',
      body: { email },
      anonymous: true,
    }),

  /**
   * Confirm a password reset using the raw token from the emailed link.
   * Throws ApiClientError (400) on an invalid/expired token.
   */
  confirmPasswordReset: (token: string, password: string) =>
    request<{ ok: boolean }>('/auth/password/reset-confirm', {
      method: 'POST',
      body: { token, password },
      anonymous: true,
    }),

  /** Verify an email address from the raw token in the emailed link. */
  verifyEmail: (token: string) =>
    request<{ ok: boolean }>('/auth/verify-email', {
      method: 'POST',
      body: { token },
      anonymous: true,
    }),

  // TODO(scaffold): refresh-token rotation + reuse detection (POST /v1/auth/refresh).
  // The MVP store treats a 401 as "session over" and routes to login.
};

// ─── Apps & Config (§3.2) ────────────────────────────────────────────────────

/** The API wraps most responses in a named object; these helpers unwrap to the value. */
export interface PutConfigResult {
  appId: string;
  configVersion: number;
  schemaVersion: number;
  requiresRebuild: string[];
}

export const appsApi = {
  list: () => request<{ apps: AppSummary[] }>('/apps').then((r) => r.apps),

  get: (appId: string) => request<{ app: AppSummary }>(`/apps/${appId}`).then((r) => r.app),

  create: (req: CreateAppRequest) =>
    request<{ appId: string; configVersion: number; schemaVersion: number }>('/apps', {
      method: 'POST',
      body: req,
    }),

  getConfig: (appId: string) =>
    request<{ config: AppConfig; version: AppVersionDto }>(`/apps/${appId}/config`).then(
      (r) => r.config,
    ),

  /** Replaces config; server creates a new immutable app_version. */
  putConfig: (appId: string, config: AppConfig) =>
    request<PutConfigResult>(`/apps/${appId}/config`, { method: 'PUT', body: config }),

  listVersions: (appId: string) =>
    request<{ versions: AppVersionDto[] }>(`/apps/${appId}/config/versions`).then(
      (r) => r.versions,
    ),

  /** Rename / change status (draft | active | archived). */
  update: (appId: string, patch: { name?: string; status?: string }) =>
    request<{ ok: true }>(`/apps/${appId}`, { method: 'PATCH', body: patch }),

  /**
   * HARD delete — permanently removes the app and ALL of its data (builds,
   * versions, signing keystore, assets). Irreversible. Resolves on 204.
   */
  remove: (appId: string) => request<void>(`/apps/${appId}`, { method: 'DELETE' }),

  // TODO(scaffold): ownership/verify and the preflight dry-run endpoint.
};

// ─── Signing: keystore mode (auto vs upload-your-own) + BYO keystore upload ──

export const signingApi = {
  get: (appId: string) => request<SigningStatusDto>(`/apps/${appId}/signing`),

  setMode: (appId: string, signingMode: SigningMode) =>
    request<SigningStatusDto>(`/apps/${appId}/signing`, { method: 'PUT', body: { signingMode } }),

  /**
   * Upload a bring-your-own keystore + credentials (multipart). The server seals the
   * material with public-key encryption — it can never read it back. Passwords are
   * write-only. Returns the refreshed signing status.
   */
  async uploadKeystore(
    appId: string,
    file: File,
    creds: { storePassword: string; keyAlias: string; keyPassword?: string },
  ): Promise<SigningStatusDto> {
    const form = new FormData();
    form.append('file', file);
    form.append('storePassword', creds.storePassword);
    form.append('keyAlias', creds.keyAlias);
    if (creds.keyPassword) form.append('keyPassword', creds.keyPassword);

    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    let res: Response;
    try {
      res = await fetch(`${API_URL}/apps/${appId}/signing/keystore`, {
        method: 'POST',
        headers, // no Content-Type → browser sets the multipart boundary
        body: form,
      });
    } catch (cause) {
      throw new ApiClientError(0, undefined, `Network error: ${(cause as Error).message}`);
    }
    if (res.status === 401) onUnauthorized();
    const text = await res.text();
    const parsed = text ? safeJson(text) : undefined;
    if (!res.ok) {
      throw new ApiClientError(res.status, parsed as ApiError | undefined, res.statusText || 'Upload failed');
    }
    return parsed as SigningStatusDto;
  },
};

// ─── Assets: multipart upload → local-disk storage on the VPS ────────────────
// (R2 is not configured on the VPS, so bytes are POSTed through the API and written
//  to disk; reads go through the public, opaque-uuid raw URL — see assetUrl.)

export const assetsApi = {
  list: (appId: string) =>
    request<{ assets: Array<{ id: string; type: string; status: string }> }>(
      `/apps/${appId}/assets`,
    ).then((r) => r.assets),

  /**
   * Upload an asset (icon / splash / onboarding / media image).
   * POSTs multipart/form-data to the API which writes the bytes to local disk and
   * records a `ready` asset row. We attach the bearer token but deliberately do NOT
   * set Content-Type, so the browser appends the correct multipart boundary.
   */
  async upload(appId: string, file: File, type: string): Promise<{ assetId: string }> {
    const form = new FormData();
    form.append('file', file);
    form.append('type', type);

    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    let res: Response;
    try {
      res = await fetch(`${API_URL}/apps/${appId}/assets/upload`, {
        method: 'POST',
        headers, // no Content-Type → browser sets multipart boundary
        body: form,
      });
    } catch (cause) {
      throw new ApiClientError(0, undefined, `Network error: ${(cause as Error).message}`);
    }

    if (res.status === 401) onUnauthorized();

    const text = await res.text();
    const parsed = text ? safeJson(text) : undefined;
    if (!res.ok) {
      throw new ApiClientError(res.status, parsed as ApiError | undefined, res.statusText || 'Asset upload failed');
    }
    return parsed as { assetId: string };
  },

  /** Stable, browser-usable URL for previewing/serving an uploaded asset (`<img src>`). */
  assetUrl(assetId: string): string {
    return `${API_URL}/assets/${assetId}/raw`;
  },
};

// ─── Builds (§3.4) ───────────────────────────────────────────────────────────

export const buildsApi = {
  /**
   * Enqueue a build. Idempotency-Key is REQUIRED by the API; we derive a stable
   * one from the app version so accidental double-clicks attach to the same job.
   */
  trigger: (appId: string, req: BuildRequest, idempotencyKey: string) =>
    request<BuildDto>(`/apps/${appId}/builds`, {
      method: 'POST',
      body: req,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),

  history: (appId: string) =>
    request<{ builds: BuildDto[] }>(`/apps/${appId}/builds`).then((r) => r.builds),

  /** Poll-friendly status read. */
  get: (buildId: string, signal?: AbortSignal) =>
    request<BuildDto>(`/builds/${buildId}`, { signal }),

  cancel: (buildId: string) =>
    request<BuildDto>(`/builds/${buildId}/cancel`, { method: 'POST' }),

  /**
   * SSE endpoint URL for live status while the tab is open. EventSource cannot
   * send an Authorization header, so the token is passed as a query param the
   * Worker also accepts on this read-only route. The authoritative completion
   * signal remains the FCM push; this is a convenience for the open tab.
   */
  eventsUrl: (buildId: string): string => {
    const token = getToken();
    const qs = token ? `?access_token=${encodeURIComponent(token)}` : '';
    return `${API_URL}/builds/${buildId}/events${qs}`;
  },

  /**
   * Resolve a short-lived, token-signed download URL for `aab` | `apk` (served by the build
   * VPS artifact server). Auth'd request → returns the URL; the caller then navigates the
   * browser to it. (Replaces the old bare-href approach, which couldn't carry the bearer token.)
   */
  artifact: (buildId: string, type: 'aab' | 'apk') =>
    request<{
      type: string;
      downloadUrl: string;
      sha256: string | null;
      certSha256: string | null;
      expiresAt: string;
    }>(`/builds/${buildId}/artifacts/${type}`),
};

// ─── Billing & Entitlement (§3.5) ────────────────────────────────────────────

export interface CheckoutSessionResponse {
  sessionId: string;
  checkoutUrl: string;
}

export interface CheckoutSessionRequest {
  /** Plan/price selector understood by the Worker (e.g. "builder_pro"). */
  planCode: string;
  /** Where Stripe returns the user after success/cancel. */
  successUrl: string;
  cancelUrl: string;
}

export const billingApi = {
  entitlement: () => request<EntitlementDto>('/billing/entitlement'),

  /** Create a Stripe Checkout session; caller redirects to checkoutUrl. */
  createCheckoutSession: (req: CheckoutSessionRequest) =>
    request<CheckoutSessionResponse>('/billing/stripe/checkout-session', {
      method: 'POST',
      body: req,
    }),

  /** Stripe customer portal (manage/cancel). */
  createPortalSession: (returnUrl: string) =>
    request<{ portalUrl: string }>('/billing/stripe/portal-session', {
      method: 'POST',
      body: { returnUrl },
    }),

  // TODO(scaffold): Play link-purchase recovery channel (mobile client only).
};
